'use client';

import { createContext, useState, useMemo, useEffect } from 'react';
import type { LivestreamingRoomData } from '@/types';
import { ACCESS_TOKEN_KEY } from '@/constants';

export interface AuthContextType {
  userLoggedIn: boolean;
  gotedoStreamsAuthToken: string | null;
  livestreamingRoomData: LivestreamingRoomData | null;
  setUserLoggedIn: (value: boolean) => void;
  setGotedoStreamsAuthToken: (value: string) => void;
  setError: (value: string) => void;
  setLivestreamingRoomData: (value: LivestreamingRoomData | null) => void;
}

export const AuthContext = createContext<AuthContextType>({
  userLoggedIn: false,
  gotedoStreamsAuthToken: null,
  livestreamingRoomData: null,
  setUserLoggedIn: () => {},
  setGotedoStreamsAuthToken: () => {},
  setLivestreamingRoomData: () => {},
  setError: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Function initializer to immediately set the auth token from local storage
  const [gotedoStreamsAuthToken, setGotedoStreamsAuthToken] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    }
    return null;
  });
  const [userLoggedIn, setUserLoggedIn] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [livestreamingRoomData, setLivestreamingRoomData] = useState<LivestreamingRoomData | null>(
    null,
  );

  useEffect(() => {
    if (gotedoStreamsAuthToken) {
      setUserLoggedIn(true);
    }
  }, [gotedoStreamsAuthToken]);

  const value = useMemo(
    () => ({
      userLoggedIn,
      gotedoStreamsAuthToken,
      livestreamingRoomData,
      setUserLoggedIn,
      setGotedoStreamsAuthToken,
      setLivestreamingRoomData,
      setError,
      error,
    }),
    [userLoggedIn, gotedoStreamsAuthToken, livestreamingRoomData, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
